<script lang="ts">
  // Cadran central : bouton de lancement au repos, jauge pendant le test.
  let {
    mode,
    value = 0,
    phase = '',
    direction = 'down',
    label = 'Lancer',
    onstart,
  }: {
    mode: 'idle' | 'busy' | 'running';
    value?: number;
    phase?: string;
    direction?: 'down' | 'up' | 'ping';
    label?: string;
    onstart?: () => void;
  } = $props();

  const R = 118;
  const C = 150;
  const START = 135;
  const SWEEP = 270;
  const max = $derived(value > 1000 ? 10000 : 1000);
  const ticks = $derived(max === 1000 ? [0, 5, 10, 50, 100, 250, 500, 1000] : [0, 10, 100, 500, 1000, 2500, 5000, 10000]);
  const frac = (v: number) => Math.min(1, Math.log10(1 + Math.max(0, v)) / Math.log10(1 + max));
  const pt = (f: number, r: number) => {
    const a = ((START + SWEEP * f) * Math.PI) / 180;
    return { x: C + r * Math.cos(a), y: C + r * Math.sin(a) };
  };
  const arc = (r: number) => {
    const s = pt(0, r);
    const e = pt(1, r);
    return `M${s.x} ${s.y} A${r} ${r} 0 1 1 ${e.x} ${e.y}`;
  };
  const LEN = (R * Math.PI * SWEEP) / 180;
  const shown = $derived(frac(value));
  const knob = $derived(pt(shown, R));
  const tickLabel = (v: number) => (v >= 1000 ? `${v / 1000}G` : String(v));
  const display = $derived(
    value >= 1000
      ? (value / 1000).toLocaleString('fr-FR', { maximumFractionDigits: 2 })
      : value.toLocaleString('fr-FR', { maximumFractionDigits: value >= 100 ? 0 : value >= 10 ? 1 : 2 }),
  );
  const unit = $derived(direction === 'ping' ? 'ms' : value >= 1000 ? 'Gbit/s' : 'Mbit/s');
  const color = $derived(direction === 'up' ? 'var(--up)' : 'var(--down)');
  const color2 = $derived(direction === 'up' ? 'var(--ring-a)' : 'var(--ring-a)');
  // Graduations courtes le long de l'arc (décoratives).
  const minor = Array.from({ length: 41 }, (_, i) => i / 40);
</script>

<div class="dial" class:live={mode !== 'idle'}>
  <span class="halo" aria-hidden="true"></span>
  {#if mode === 'idle'}
    <span class="orbits" aria-hidden="true"><i></i><i></i><i></i></span>
    <button class="go" onclick={onstart}>
      <span class="ring" aria-hidden="true"></span>
      <span class="core"><span class="label">{label}<span class="sr-only"> le test</span></span></span>
    </button>
    <span class="hint" aria-hidden="true">débit · latence · réactivité</span>
  {:else}
    <svg viewBox="0 0 300 300" aria-hidden="true">
      <defs>
        <linearGradient id="trace" x1="0" y1="1" x2="1" y2="0">
          <stop offset="0" stop-color={color2} />
          <stop offset="1" stop-color={color} />
        </linearGradient>
        <filter id="glow" x="-30%" y="-30%" width="160%" height="160%">
          <feGaussianBlur stdDeviation="6" result="b" />
          <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
      </defs>
      {#each minor as f}
        {@const a = pt(f, R + 14)}
        {@const b = pt(f, R + (Math.round(f * 40) % 5 === 0 ? 22 : 18))}
        <line x1={a.x} y1={a.y} x2={b.x} y2={b.y} class="minor" class:lit={mode === 'running' && direction !== 'ping' && f <= shown} />
      {/each}
      <path d={arc(R)} fill="none" stroke="var(--surface-2)" stroke-width="14" stroke-linecap="round" />
      {#if mode === 'running' && direction !== 'ping'}
        <path d={arc(R)} fill="none" stroke="url(#trace)" stroke-width="14" stroke-linecap="round" stroke-dasharray="{LEN * shown} {LEN}" class="fill" filter="url(#glow)" />
        <circle cx={knob.x} cy={knob.y} r="8" fill="#fff" class="knob" style="filter: drop-shadow(0 0 8px {color})" />
        {#each ticks as tv}
          {@const p = pt(frac(tv), R - 28)}
          <text x={p.x} y={p.y + 4} text-anchor="middle" class="tick">{tickLabel(tv)}</text>
        {/each}
      {:else}
        <path d={arc(R)} fill="none" stroke="url(#trace)" stroke-width="14" stroke-linecap="round" stroke-dasharray="46 {LEN}" class="spin" filter="url(#glow)" />
      {/if}
    </svg>
    <div class="center" aria-hidden="true">
      {#if mode === 'running'}
        <div class="value" style="--c: {color}">{display}</div>
        <div class="unit">{unit}</div>
      {/if}
    </div>
    <div class="phase" style="--c: {color}" aria-hidden="true">
      {#if direction === 'down' && mode === 'running'}<svg viewBox="0 0 24 24" width="18" height="18"><path d="M12 4v15m-6-6 6 6 6-6" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" /></svg>{/if}
      {#if direction === 'up' && mode === 'running'}<svg viewBox="0 0 24 24" width="18" height="18"><path d="M12 20V5m-6 6 6-6 6 6" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" /></svg>{/if}
      {phase}
    </div>
  {/if}
</div>

<style>
  .dial { container-type: inline-size; position: relative; width: min(80vw, 340px); aspect-ratio: 1; margin: 0 auto; display: grid; place-items: center; }
  .dial > svg { width: 100%; height: 100%; display: block; overflow: visible; }
  .phase svg { width: 18px; height: 18px; flex: none; }

  /* Halo de fond, plus intense pendant la mesure */
  .halo {
    position: absolute;
    inset: 4%;
    border-radius: 50%;
    background: conic-gradient(from 0deg, var(--ring-a), var(--ring-b), var(--ring-c), var(--ring-a));
    filter: blur(42px);
    opacity: 0.32;
    animation: tourne 14s linear infinite, respire 4s ease-in-out infinite;
  }
  .live .halo { opacity: 0.22; }

  /* Bouton de lancement : anneau néon tournant + cœur en verre */
  .go {
    grid-area: 1 / 1;
    width: 74%;
    aspect-ratio: 1;
    border-radius: 50%;
    border: 0;
    padding: 0;
    background: transparent;
    display: grid;
    place-items: center;
    overflow: visible;
    z-index: 1;
    backdrop-filter: none;
    transition: transform 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  }
  .go:hover { background: transparent; transform: scale(1.04); }
  .go:active { transform: scale(0.98); }
  .ring {
    position: absolute;
    inset: 0;
    border-radius: 50%;
    padding: 3px;
    background: conic-gradient(from 0deg, var(--ring-a), var(--ring-b), var(--ring-c), var(--ring-a));
    -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
    mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    animation: tourne 6s linear infinite;
  }
  .go:hover .ring { animation-duration: 2s; }
  .core {
    position: absolute;
    inset: 3px;
    border-radius: 50%;
    display: grid;
    place-content: center;
    padding-bottom: 6cqi;
    background:
      radial-gradient(circle at 30% 25%, color-mix(in oklab, var(--ring-b) 16%, transparent), transparent 55%),
      radial-gradient(circle at 70% 80%, color-mix(in oklab, var(--ring-c) 14%, transparent), transparent 55%),
      color-mix(in oklab, var(--surface-solid) 82%, transparent);
    backdrop-filter: blur(12px);
    box-shadow: inset 0 0 40px color-mix(in oklab, var(--ring-a) 22%, transparent);
  }
  .label { font-family: var(--font-display); font-size: 14.5cqi; font-weight: 500; letter-spacing: 0.01em; color: var(--text); line-height: 1; }
  .hint { grid-area: 1 / 1; z-index: 2; pointer-events: none; transform: translateY(9.5cqi); font-size: 3.1cqi; text-transform: uppercase; letter-spacing: 0.12em; color: var(--muted); }

  /* Particules en orbite */
  .orbits { position: absolute; inset: 0; pointer-events: none; }
  .orbits i { position: absolute; inset: 6%; border-radius: 50%; animation: tourne 9s linear infinite; }
  .orbits i::before { content: ''; position: absolute; top: -4px; left: 50%; width: 8px; height: 8px; margin-left: -4px; border-radius: 50%; background: var(--ring-b); box-shadow: 0 0 14px 2px var(--ring-b); }
  .orbits i:nth-child(2) { inset: 1%; animation-duration: 13s; animation-direction: reverse; }
  .orbits i:nth-child(2)::before { background: var(--ring-c); box-shadow: 0 0 14px 2px var(--ring-c); width: 6px; height: 6px; }
  .orbits i:nth-child(3) { inset: 10%; animation-duration: 17s; animation-delay: -6s; }
  .orbits i:nth-child(3)::before { background: var(--ring-a); box-shadow: 0 0 14px 2px var(--ring-a); width: 5px; height: 5px; }

  /* Jauge */
  .minor { stroke: var(--border); stroke-width: 2; stroke-linecap: round; transition: stroke 0.2s; }
  .minor.lit { stroke: var(--down); }
  .fill { transition: stroke-dasharray 0.18s linear; }
  .knob { transition: cx 0.18s linear, cy 0.18s linear; }
  .spin { transform-origin: 150px 150px; animation: tourne 1.1s linear infinite; }
  .tick { font-size: 11px; fill: var(--muted); font-family: var(--font-display); font-variant-numeric: tabular-nums; }
  .center { position: absolute; inset: 0; display: flex; flex-direction: column; align-items: center; justify-content: center; padding-bottom: 6px; }
  .value {
    font-family: var(--font-display);
    font-size: clamp(2.6rem, 12vw, 3.6rem);
    font-weight: 700;
    letter-spacing: -0.04em;
    font-variant-numeric: tabular-nums;
    line-height: 1;
    color: var(--text);
    text-shadow: 0 0 28px color-mix(in oklab, var(--c) 55%, transparent);
  }
  .unit { color: var(--muted); font-size: 0.95rem; margin-top: 0.35rem; }
  .phase { position: absolute; bottom: 6%; left: 0; right: 0; display: flex; justify-content: center; align-items: center; gap: 0.3rem; font-weight: 650; color: var(--c); font-size: 0.95rem; }

  @keyframes tourne { to { transform: rotate(360deg); } }
  @keyframes respire { 50% { opacity: 0.45; } }
  @media (prefers-reduced-motion: reduce) { .halo, .ring, .orbits i, .spin { animation: none; } }
</style>
